import { ApiResult, HttpClient } from "../http.js";
import { CompanyPermissionConfigDto, UserPermissionOverrideDto } from "../types/permissions.js";

/**
 * Full-set replace payload for a role's granted permissions. The admin/team PUTs are
 * absolute: `permissions` is the complete list of granted keys for that role, not a delta.
 * Callers seed the current set from {@link PermissionsApi.getConfig} (the authoritative
 * persisted config; absent roles/teams fall back to `@mesterplan/shared-types` defaults),
 * apply the toggle, and send the full true-key set.
 */
export interface SetRolePermissionsPayload {
  permissions: string[];
}

/**
 * Single-key delta for a role/team. `value: true` grants, `false` revokes; the server applies
 * it to the role's current effective set.
 *
 * Prefer this over {@link SetRolePermissionsPayload} for a UI toggle. The full-set PUT forces a
 * client-side read-modify-write across two requests, so two admins toggling different keys
 * concurrently both seed from the same baseline and the second write silently clobbers the
 * first. Sending only the delta means the client never holds — and so can never overwrite —
 * the rest of the set.
 */
export interface TogglePermissionPayload {
  permission: string;
  value: boolean;
}

/**
 * Upsert payload for a single user's override. `granted` keys are added on top of the
 * user's role/team default; `revoked` keys are subtracted. Both are full lists (absolute),
 * so callers read the current override via {@link PermissionsApi.getUserOverride} and merge.
 */
export interface UpsertUserOverridePayload {
  granted: string[];
  revoked: string[];
}

/**
 * Wraps the authenticated `/api/permissions` controller — the company's role/team granted
 * sets and per-user overrides. Replaces the granular Firestore `setDoc`/`updateDoc` writes
 * the SPA permission-settings hooks used to issue against the company doc + the
 * `USER_PERMISSION_OVERRIDES` subcollection.
 *
 * Impedance note: the controller replaces whole sets, while the UI toggles single keys. For
 * both admin/team config and per-user overrides the hook layer reads the current persisted
 * set from the API ({@link PermissionsApi.getConfig} / {@link PermissionsApi.getUserOverride}),
 * applies the single change, and PUTs the full set — so enforcement matches the persisted
 * state, not a stale or default-only client view.
 */
export class PermissionsApi {
  constructor(private readonly http: HttpClient) {}

  /**
   * The company's role/team permission config (admin granted set + per-team granted sets).
   * Never 404s: a brand-new company returns empty `admin`/`teams`, and absent roles/teams
   * fall back to the in-code defaults on both server and client.
   */
  getConfig(): Promise<ApiResult<CompanyPermissionConfigDto>> {
    return this.http.get<CompanyPermissionConfigDto>("/api/permissions/config");
  }

  /** A single user's override, or a 404 `ApiResult` when the user has none. */
  getUserOverride(userId: string): Promise<ApiResult<UserPermissionOverrideDto>> {
    return this.http.get<UserPermissionOverrideDto>(
      `/api/permissions/users/${encodeURIComponent(userId)}`,
    );
  }

  /** Replace the admin role's granted set. Must include `settings.managePermissions`. */
  setAdmin(payload: SetRolePermissionsPayload): Promise<ApiResult<string>> {
    return this.http.put<string>("/api/permissions/admin", { body: payload });
  }

  /**
   * Replace a team's granted set. `teamId` is the team's `teams.id` guid. A guid outside the
   * caller's active org answers 404. The slug form of this route is a transitional shim,
   * removed in PR7.
   */
  setTeam(teamId: string, payload: SetRolePermissionsPayload): Promise<ApiResult<string>> {
    return this.http.put<string>(
      `/api/permissions/teams/${encodeURIComponent(teamId)}`,
      { body: payload },
    );
  }

  /**
   * Grant or revoke ONE admin permission. The server applies the delta to the current
   * effective set, so no read-modify-write is needed client-side. Preferred over
   * {@link setAdmin} for UI toggles — see {@link TogglePermissionPayload}.
   */
  toggleAdmin(payload: TogglePermissionPayload): Promise<ApiResult<string>> {
    return this.http.patch<string>("/api/permissions/admin", { body: payload });
  }

  /**
   * Grant or revoke ONE permission for a team. `teamId` is the team's `teams.id` guid. A guid
   * outside the caller's active org answers 404. The slug form of this route is a transitional
   * shim, removed in PR7. Preferred over {@link setTeam} for UI toggles.
   */
  toggleTeam(teamId: string, payload: TogglePermissionPayload): Promise<ApiResult<string>> {
    return this.http.patch<string>(
      `/api/permissions/teams/${encodeURIComponent(teamId)}`,
      { body: payload },
    );
  }

  /**
   * Reset the admin role to the server's in-code default set. Resolved server-side so the
   * default is the C# baseline the resolver enforces, never a client-supplied set.
   */
  resetAdmin(): Promise<ApiResult<string>> {
    return this.http.post<string>("/api/permissions/admin/reset");
  }

  /**
   * Reset a team to the server's in-code default set (clears the override so the resolver
   * falls back to its team default). `teamId` is the team's `teams.id` guid. A guid outside
   * the caller's active org answers 404. The slug form of this route is a transitional shim,
   * removed in PR7.
   */
  resetTeam(teamId: string): Promise<ApiResult<string>> {
    return this.http.post<string>(
      `/api/permissions/teams/${encodeURIComponent(teamId)}/reset`,
    );
  }

  /** Upsert a user's override (full granted/revoked lists). */
  setUserOverride(userId: string, payload: UpsertUserOverridePayload): Promise<ApiResult<string>> {
    return this.http.put<string>(
      `/api/permissions/users/${encodeURIComponent(userId)}`,
      { body: payload },
    );
  }

  /** Delete a user's override entirely (reset to role/team default). */
  deleteUserOverride(userId: string): Promise<ApiResult<boolean>> {
    return this.http.delete<boolean>(
      `/api/permissions/users/${encodeURIComponent(userId)}`,
    );
  }
}
